import { lodApi } from "@/shared/api";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useUpdateEffect } from "react-use";
import {
  convertDateToUTC,
  parseFilterValue,
  parseSkuIds,
} from "../lib/helpers";

export const usePaginatedQuery = <T>(
  queryParameters: Record<string, any>,
  queryOptions: Record<string, any>,
  onUpdate?: () => void
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(() =>
    searchParams.get("page") ? Number(searchParams.get("page")) - 1 : 0
  );
  const [pageSize, setPageSize] = useState(
    searchParams.get("limit") ? Number(searchParams.get("limit")) : 20
  );

  const [trigger, queryResult] =
    lodApi.endpoints.dashboardOrders.useLazyQuery(queryOptions); // https://redux-toolkit.js.org/rtk-query/api/created-api/hooks#uselazyquery

  const count = queryResult.data?.count || 0;
  const currentData = queryResult.data?.results || [];
  const totalPages = Math.ceil(count / pageSize);

  useEffect(() => {
    // Add a subscription
    trigger({
      from_datetime: convertDateToUTC(queryParameters.from_datetime),
      to_datetime: convertDateToUTC(queryParameters.to_datetime),
      order_source: parseFilterValue(queryParameters.order_source),
      annotator_id: parseFilterValue(queryParameters.annotator_id),
      order_id: parseFilterValue(queryParameters.order_id),
      stage_journey_annotation: parseFilterValue(
        queryParameters.stage_journey_annotation
      ),
      stage_transaction_annotation: parseFilterValue(
        queryParameters.stage_transaction_annotation
      ),
      stage_track_persistence_annotation: parseFilterValue(
        queryParameters.stage_track_persistence_annotation
      ),
      sku_ids: parseSkuIds(queryParameters.sku_ids),
      journeys_count: parseFilterValue(queryParameters.journeys_count),
      page: page + 1,
      limit: pageSize,
      ordering: queryParameters.ordering,
    }).unwrap();

    searchParams.set("page", String(page + 1));
    searchParams.set("limit", String(pageSize));

    setSearchParams(searchParams, {
      replace: true,
    });
    onUpdate?.();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, JSON.stringify(queryParameters)]);

  useUpdateEffect(() => {
    if (page === 0) {
      trigger({
        page: 1,
        limit: pageSize,
        ordering: queryParameters.ordering,
        annotator_id: parseFilterValue(queryParameters.annotator_id),
        from_datetime: convertDateToUTC(queryParameters.from_datetime),
        to_datetime: convertDateToUTC(queryParameters.to_datetime),
        order_source: parseFilterValue(queryParameters.order_source),
        journeys_count: parseFilterValue(queryParameters.journeys_count),
        stage_journey_annotation: parseFilterValue(
          queryParameters.stage_journey_annotation
        ),
        stage_transaction_annotation: parseFilterValue(
          queryParameters.stage_transaction_annotation
        ),
        stage_track_persistence_annotation: parseFilterValue(
          queryParameters.stage_track_persistence_annotation
        ),
        sku_ids: parseSkuIds(queryParameters.sku_ids),
        order_id: parseFilterValue(queryParameters.order_id),
      }).unwrap();

      const search = new URLSearchParams(searchParams);

      search.set("page", String(page + 1));
      search.set("limit", String(pageSize));

      setSearchParams(search);
    } else {
      setPage(0);
    }
    onUpdate?.();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  return {
    totalPages,
    totalCount: count,
    currentPage: page,
    setCurrentPage: setPage,
    data: currentData as T[],
    queryResult,
    pageSize,
    setPageSize,
  };
};
